import { BarChart3, ExternalLink } from 'lucide-react'
import { Button, Drawer, MarginIndicator } from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import type { OpenOrderRow } from '../api/types'
import { StateCell } from './StateCell'

export interface OrderDrawerProps {
  row: OpenOrderRow | null
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  /** Open the ProductCoverageDrawer for this row's product. Parent
   *  should close OrderDrawer at the same time so only one drawer is
   *  on-screen — see OpenOrdersPage's onViewCoverage wiring. */
  onViewCoverage?: (productId: number) => void
}

export function OrderDrawer({
  row,
  onClose,
  onPrev,
  onNext,
  onViewCoverage,
}: OrderDrawerProps) {
  return (
    <Drawer
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      ident={row?.so_number}
      title={row?.customer ?? ''}
      meta={
        row ? (
          <>
            <span>{row.po_number ?? '—'}</span>
            <span className="opacity-40">·</span>
            <span>Due {row.due_on ?? '—'}</span>
          </>
        ) : undefined
      }
      onPrev={onPrev}
      onNext={onNext}
      headerActions={
        row ? (
          <div className="flex items-center gap-1.5">
            {onViewCoverage ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewCoverage(row.product_id)}
                title="See all open demand + supply for this product"
              >
                <BarChart3 size={12} />
                Coverage
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.open(odooUrl('sale.order', row.so_id), '_blank')}
            >
              Open in Odoo
              <ExternalLink size={12} />
            </Button>
          </div>
        ) : undefined
      }
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {row ? <OrderDetail row={row} /> : null}
    </Drawer>
  )
}

function OrderDetail({ row }: { row: OpenOrderRow }) {
  return (
    <div className="flex flex-col gap-5">
      <section>
        <SectionLabel>State</SectionLabel>
        <StateCell row={row} />
      </section>

      <section>
        <SectionLabel>Order</SectionLabel>
        <KvGrid
          rows={[
            ['Customer', row.customer],
            [
              'SO',
              <a
                key="so"
                href={odooUrl('sale.order', row.so_id)}
                target="_blank"
                rel="noreferrer"
                className="text-link hover:underline"
              >
                {row.so_number}
              </a>,
            ],
            ['Line', String(row.line)],
            ['P.O.', row.po_number ?? '—'],
            ['Customer part#', row.customer_part_number ?? '—'],
            [
              'Product',
              <a
                key="product"
                href={odooUrl('product.product', row.product_id)}
                target="_blank"
                rel="noreferrer"
                className="text-link hover:underline"
              >
                {row.product_name}
              </a>,
            ],
            ['Due', row.due_on ?? '—'],
            ['Order confirmed', row.confirmation_date ?? '—'],
            ['Last revision', row.last_revision ?? '—'],
          ]}
        />
      </section>

      <section>
        <SectionLabel>Quantities</SectionLabel>
        <KvGrid
          rows={[
            ['Ordered', formatQty(row.ordered, row.uom_name)],
            ['Open', formatQty(row.open, row.uom_name)],
            ['On hand', formatQty(row.on_hand, row.on_hand_uom ?? row.uom_name)],
            ['Replenish route', row.replenish_on_order ? 'Yes' : 'No'],
          ]}
        />
      </section>

      <section>
        <SectionLabel>Vendor coverage</SectionLabel>
        <KvGrid
          rows={[
            ['Vendor', row.vendor ?? '—'],
            ['Vendor PO', row.vendor_po ?? '—'],
            ['PO due', row.po_due ?? '—'],
          ]}
        />
      </section>

      {typeof row.open === 'number' && row.ordered > 0 ? (
        <section>
          <SectionLabel>Fulfillment</SectionLabel>
          <div className="flex items-center gap-3">
            <span className="text-xs text-tertiary">
              {Math.round(((row.ordered - row.open) / row.ordered) * 100)}% delivered
            </span>
            <MarginIndicator
              value={((row.ordered - row.open) / row.ordered) * 100}
              thresholds={{ danger: 50, warning: 90 }}
              showBar
            />
          </div>
        </section>
      ) : null}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 border-b border-border-subtle pb-1 text-role-section-label">
      {children}
    </div>
  )
}

function KvGrid({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-1 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-tertiary">{k}</dt>
          <dd className="text-primary tabular-nums">{v}</dd>
        </div>
      ))}
    </dl>
  )
}

function formatQty(q: number | null | undefined, uom: string | null | undefined): string {
  if (typeof q !== 'number' || !Number.isFinite(q)) return '—'
  const rounded = Number.isInteger(q) ? q.toString() : q.toFixed(2)
  return uom ? `${rounded} ${uom}` : rounded
}
